// src/app/modules/demandes/HorsCatalogue.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/HorsCatalogue.css";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { createDemandeCollabRequest } from "../../../../../redux/actions/demandesActions";

const initial = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
    mode: "",
    period: "",
    niveau: "",
    certifiante: "",
    priorite: 2,
    objectifs: "",
    objectifs2: "",
    objectifs3: "",
};

const PERIOD_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const NIVEAU_OPTIONS = ["Débutant", "Intermédiaire", "Avancé"];
const MODE_OPTIONS = ["Distanciel", "Présentiel", "Hybride"];

export default function HorsCatalogue({ campagneId: campagneIdProp = null, hideCampagneSelect = false }) {
    const dispatch = useDispatch();

    const { currentUser } = useSelector((s) => s.user || {});
    const campagnesState = useSelector((s) => s.campagnes || {});
    const paramdomainethemes = useSelector((s) => s.paramDomainesThemes || {});

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const domaines = paramdomainethemes.domaines || [];
    const themes = paramdomainethemes.themes || [];

    const [form, setForm] = useState(initial);

    const embedded = !!hideCampagneSelect;

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchCataloguesRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (campagneIdProp != null) return;
        if (!form.campagneId && campagnes?.length) {
            setForm((s) => ({ ...s, campagneId: String(campagnes[0].id) }));
        }
    }, [campagnes, form.campagneId, campagneIdProp]);

    const campagneId = campagneIdProp ?? (form.campagneId ? Number(form.campagneId) : null);

    const themesFiltered = useMemo(() => {
        const did = form.domaineId ? Number(form.domaineId) : null;
        const list = Array.isArray(themes) ? themes : [];
        if (!did) return list;
        return list.filter((t) => Number(t?.domaineId || t?.domaine?.id) === did);
    }, [themes, form.domaineId]);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = (e) => {
        e.preventDefault();

        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Sélectionne d'abord une campagne." });
            return;
        }
        if (!form.action?.trim()) {
            Swal.fire({ icon: "warning", text: "Action / Intitulé est obligatoire." });
            return;
        }

        const payload = {
            campagneId,
            type: "HORS_CATALOGUE",
            domaineId: form.domaineId ? Number(form.domaineId) : null,
            themeId: form.themeId ? Number(form.themeId) : null,
            action: form.action.trim(),
            description: form.description?.trim() || null,
            mode: form.mode || null,
            period: form.period || null,
            niveau: form.niveau || null,
            certifiante: form.certifiante === "true" ? true : form.certifiante === "false" ? false : null,
            priorite: form.priorite ? Number(form.priorite) : null,
            objectifs: form.objectifs || null,
            objectifs2: form.objectifs2 || null,
            objectifs3: form.objectifs3 || null,
        };

        dispatch(createDemandeCollabRequest(payload));
    };

    return (
        <div className={`hc-page ${embedded ? "is-embedded" : ""}`.trim()}>
            <form className="hc-card" onSubmit={submit}>

                {/* ── Informations principales ── */}
                <p className="hc-section-label">Informations principales</p>

                <div className="hc-field">
                    <label>Action / Intitulé *</label>
                    <input
                        value={form.action}
                        onChange={(e) => update("action", e.target.value)}
                        placeholder="Ex: Formation spécifique XYZ..."
                    />
                </div>

                <div className="hc-field">
                    <label>Description</label>
                    <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={4} />
                </div>

                {/* ── Paramètres ── */}
                <p className="hc-section-label">Paramètres</p>

                <div className="hc-grid-3">
                    {!hideCampagneSelect && (
                        <div className="hc-field">
                            <label>Campagne *</label>
                            <select value={form.campagneId} onChange={(e) => update("campagneId", e.target.value)}>
                                <option value="">—</option>
                                {(campagnes || []).map((c) => (
                                    <option key={c.id} value={c.id}>{c.nom || c.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="hc-field">
                        <label>Période</label>
                        <select value={form.period || ""} onChange={(e) => update("period", e.target.value)}>
                            <option value="">—</option>
                            {PERIOD_OPTIONS.map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Domaine</label>
                        <select
                            value={form.domaineId}
                            onChange={(e) => {
                                update("domaineId", e.target.value);
                                update("themeId", "");
                            }}
                        >
                            <option value="">—</option>
                            {(domaines || []).map((d) => (
                                <option key={d.id} value={d.id}>{d.nom || d.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Thème</label>
                        <select value={form.themeId} onChange={(e) => update("themeId", e.target.value)}>
                            <option value="">—</option>
                            {(themesFiltered || []).map((t) => (
                                <option key={t.id} value={t.id}>{t.nom || t.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Mode</label>
                        <select value={form.mode || ""} onChange={(e) => update("mode", e.target.value)}>
                            <option value="">—</option>
                            {MODE_OPTIONS.map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Niveau</label>
                        <select value={form.niveau || ""} onChange={(e) => update("niveau", e.target.value)}>
                            <option value="">—</option>
                            {NIVEAU_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Priorité</label>
                        <select value={form.priorite} onChange={(e) => update("priorite", e.target.value)}>
                            <option value={1}>1 — Haute</option>
                            <option value={2}>2 — Moyenne</option>
                            <option value={3}>3 — Basse</option>
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Certifiante</label>
                        <div className="hc-radio-group">
                            {[
                                { value: "true",  label: "Oui" },
                                { value: "false", label: "Non" },
                            ].map(({ value, label }) => (
                                <label key={value} className="hc-radio-label">
                                    <input
                                        type="radio"
                                        name="certifiante"
                                        value={value}
                                        checked={form.certifiante === value}
                                        onChange={() => update("certifiante", value)}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Objectifs ── */}
                <p className="hc-section-label">Objectifs</p>

                <div className="hc-grid-3">
                    <div className="hc-field">
                        <label>Objectifs (1)</label>
                        <textarea value={form.objectifs} onChange={(e) => update("objectifs", e.target.value)} rows={2} />
                    </div>
                    <div className="hc-field">
                        <label>Objectifs (2)</label>
                        <textarea value={form.objectifs2} onChange={(e) => update("objectifs2", e.target.value)} rows={2} />
                    </div>
                    <div className="hc-field">
                        <label>Objectifs (3)</label>
                        <textarea value={form.objectifs3} onChange={(e) => update("objectifs3", e.target.value)} rows={2} />
                    </div>
                </div>

                <div className="hc-actions">
                    <button className="hc-btn hc-btn-primary" type="submit" disabled={!campagneId}>
                        Envoyer la demande
                    </button>
                </div>

                {!campagneId && <div className="demande-hint">⚠️ Sélectionne d'abord une campagne.</div>}
            </form>
        </div>
    );
}